export enum UserType {
  CUSTOMER = 'customer',
  STAFF = 'staff',
}

export enum StaffRole {
  LOAN_OFFICER = 'loan_officer',
  MANAGER = 'manager',
  ADMIN = 'admin',
}

export enum ApplicationStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  UNDER_REVIEW = 'under_review',
  APPROVED = 'approved',
  DECLINED = 'declined',
  DISBURSED = 'disbursed',
}

export enum LoanStatus {
  ACTIVE = 'active',
  PAID_OFF = 'paid_off',
  DEFAULTED = 'defaulted',
  WRITTEN_OFF = 'written_off',
}

export enum PaymentMethod {
  BANK_TRANSFER = 'bank_transfer',
  CARD = 'card',
  USSD = 'ussd',
  CASH = 'cash',
  MOBILE_MONEY = 'mobile_money',
}

export interface User {
  user_id: string;
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  date_of_birth?: string;
  gender?: 'Male' | 'Female' | 'Other' | 'Prefer not to say';
  marital_status?: 'Single' | 'Married' | 'Divorced' | 'Widowed';
  bvn?: string;
  user_type: UserType;
  staff_role?: StaffRole;
}

export interface AuthResponse {
  user: User;
  token: string;
}

export interface LoanProduct {
  product_id: string;
  product_type: string;
  product_name: string;
  description: string;
  min_amount: string | number;
  max_amount: string | number;
  min_term_months: number;
  max_term_months: number;
  min_interest_rate: string | number;
  max_interest_rate: string | number;
  processing_fee_percentage: string | number;
  requires_collateral: boolean;
  requires_guarantor: boolean;
  required_guarantors_count?: number;
  target_audience: string;
  required_documents: string[];
}

export interface Guarantor {
  name: string;
  email: string;
  phone: string;
}

export interface CustomerProfile {
  profile_id: string;
  user_id: string;
  address?: string;
  city?: string;
  state?: string;
  lga?: string;
  employer?: string;
  occupation?: string;
  monthly_income?: string;
  monthly_expenses?: string;
  dependents?: number;
  employment_status?: 'Employed' | 'Self-Employed' | 'Business Owner' | 'Unemployed';
  employment_start_date?: string;
  work_address?: string;
  business_name?: string;
  business_type?: string;
  business_registration_number?: string;
  tax_identification_number?: string;
  years_in_business?: number;
  bank_name?: string;
  account_number?: string;
  account_name?: string;
  bvn?: string;
  bvn_verified?: boolean;
  bvn_verified_at?: string;
  next_of_kin_name?: string;
  next_of_kin_phone?: string;
  next_of_kin_relationship?: string;
  created_at?: string;
  updated_at?: string;
}

export interface LoanApplication {
  application_id: string;
  application_reference: string;
  user_id: string;
  product_id: string;
  amount_requested: string;
  loan_term_months: number;
  purpose?: string;
  monthly_income?: string;
  existing_loans_count?: number;
  existing_loans_total?: string;
  // Business-specific fields
  business_revenue?: string;
  lpo_value?: string;
  lpo_client_name?: string;
  lpo_client_contact?: string;
  contract_value?: string;
  contract_client_name?: string;
  contract_client_contact?: string;
  invoice_amount?: string;
  invoice_number?: string;
  invoice_due_date?: string;
  invoice_debtor_name?: string;
  invoice_debtor_contact?: string;
  project_timeline?: string;
  expected_completion_date?: string;
  status: ApplicationStatus;
  submitted_at?: string;
  created_at: string;
  updated_at?: string;
  product_name?: string;
  product_type?: string;
  // Draft data for form progress persistence
  draft_data?: any;
}

export interface Loan {
  loan_id: string;
  loan_reference: string;
  principal_amount: string;
  interest_rate: string;
  loan_term_months: number;
  monthly_payment: string;
  disbursement_date: string;
  maturity_date: string;
  outstanding_principal: string;
  outstanding_interest: string;
  total_paid: string;
  status: LoanStatus;
  is_overdue: boolean;
  product_name?: string;
}

export interface PaymentSchedule {
  schedule_id: string;
  installment_number: number;
  due_date: string;
  principal_amount: string;
  interest_amount: string;
  total_amount: string;
  amount_paid: string;
  is_paid: boolean;
  paid_date?: string;
  is_overdue: boolean;
}

export interface Payment {
  payment_id: string;
  payment_reference: string;
  amount: string;
  payment_method: PaymentMethod;
  payment_date: string;
  status: string;
  bank_name?: string;
  payment_proof_url?: string;
  verified_at?: string;
  verification_notes?: string;
  transaction_reference?: string;
}

export interface UserStats {
  total_loans: string;
  active_loans: string;
  paid_loans: string;
  total_outstanding: string;
  total_paid: string;
  pending_applications: number;
  upcoming_payment?: {
    due_date: string;
    total_amount: string;
    loan_reference: string;
  };
}

export interface AdminStats {
  loans: {
    total_loans: string;
    active_loans: string;
    paid_loans: string;
    defaulted_loans: string;
    total_disbursed: string;
    total_outstanding: string;
    total_collected: string;
  };
  applications: {
    total_applications: string;
    pending_applications: string;
    approved_applications: string;
    declined_applications: string;
  };
  customers: {
    total_customers: string;
  };
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

export interface PaginatedResponse<T = any> {
  success: boolean;
  message: string;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface BVNVerification {
  verification_id: string;
  user_id: string;
  bvn: string;
  verification_status: 'pending' | 'success' | 'failed' | 'mismatch';
  verified_name?: string;
  verified_dob?: string;
  verified_phone?: string;
  verified_photo_url?: string;
  verification_response?: any;
  verification_error?: string;
  name_match_score?: number;
  dob_matched?: boolean;
  phone_matched?: boolean;
  verification_provider: string;
  verified_at: string;
  created_at: string;
}

export interface AccountVerification {
  verification_id: string;
  user_id: string;
  bank_name: string;
  account_number: string;
  account_name?: string;
  bvn?: string;
  verification_status: 'pending' | 'success' | 'failed';
  verification_response?: any;
  verification_error?: string;
  verification_provider: string;
  verified_at: string;
  created_at: string;
}

export interface NigerianState {
  code: string;
  name: string;
  lgas: string[];
}

export interface NigerianBank {
  name: string;
  code: string;
}
